import axios from 'axios'
import {
  attachInterceptors,
  attachRequestInterceptor,
  attachResponseInterceptor
} from '@/services/api/interceptors'
import {
  getPartnerImpersonationToken,
  mintOnce,
  setPartnerImpersonationToken
} from '@/services/api/partner-impersonation-cache'
import { attachFleetTransport } from '@/services/api/fleet-transport'

// Get V3 API Base URL
const getV3ApiBaseURL = () => {
  // Use the environment variable directly
  return import.meta.env.VITE_TATTLE_V3_API_BASE_URL || ''
}

// Get Integrations API Base URL
const getIntegrationsApiBaseURL = () => {
  // Use the environment variable directly
  return import.meta.env.VITE_INTEGRATIONS_API_BASE_URL || ''
}

// Get Partners API Base URL
const getPartnersApiBaseURL = () => {
  // Use the environment variable directly
  return import.meta.env.VITE_PARTNERS_API_BASE_URL || ''
}

// Get Tasks API Base URL
const getTasksApiBaseURL = () => {
  // Use the environment variable directly
  return import.meta.env.VITE_TASKS_API_BASE_URL || ''
}

// Get Surveys API Base URL (MenuLevelFeedback API)
const getSurveysApiBaseURL = () => {
  // Use the environment variable directly
  return import.meta.env.VITE_SURVEYS_API_BASE_URL || ''
}

// Claude fleet control API (tattle-claude-fleet), which verifies the admin's own Cognito token
const getFleetApiBaseURL = () => {
  return import.meta.env.VITE_FLEET_API_BASE_URL || ''
}

// Get Inbox API Base URL (in-house email sink, dev/staging only)
const getInboxApiBaseURL = () => {
  return import.meta.env.VITE_INBOX_API_BASE_URL || ''
}

// Shared X-Test-Api-Key for the inbox sink. Same value as the sink's
// TEST_API_KEY env var; loaded from SSM in deployed envs.
const getInboxApiKey = () => {
  return import.meta.env.VITE_INBOX_API_KEY || ''
}

// Legacy function for backward compatibility
const getTattleBaseURL = () => {
  return getV3ApiBaseURL()
}

// Create HTTP clients for different API services
const createHttpClient = (baseURL: string) => {
  const client = axios.create({
    baseURL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  })

  attachInterceptors(client)

  return client
}

// V3 API HTTP client (for /v3/api endpoints)
const v3ApiHttp = createHttpClient(getV3ApiBaseURL())

// Integrations API HTTP client (for /integrations/api endpoints)
const integrationsApiHttp = createHttpClient(getIntegrationsApiBaseURL())

// Partners API HTTP client (for /partners/api endpoints)
const partnersApiHttp = createHttpClient(getPartnersApiBaseURL())

// Tasks API HTTP client (for /tasks/api endpoints)
const tasksApiHttp = createHttpClient(getTasksApiBaseURL())

// Surveys API HTTP client (for /surveys/api endpoints - MenuLevelFeedback)
const surveysApiHttp = createHttpClient(getSurveysApiBaseURL())

/**
 * Claude Fleet API client (tattle-claude-fleet). Always the admin's own Cognito token, even while
 * impersonating: the fleet checks who the admin is. And no session handling: the fleet answers 401
 * for a brief failure too (its token check could not run) and the run drawer polls every 5 seconds, so
 * treating one as an expired session would sign the admin out or end an impersonation. The fleet store
 * shows the fleet's own message for every error instead, 403 included.
 */
const fleetApiHttp = axios.create({
  baseURL: getFleetApiBaseURL(),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})
attachInterceptors(fleetApiHttp, { ownSession: true, skipAuthHandling: true })
attachFleetTransport(fleetApiHttp)

/**
 * The fleet client for counters ADX sends in the background (a Claude page opened). Same token as
 * fleetApiHttp, but outside its transport, so a count does not drop the fleet reads every page shares,
 * and no response handling, so a failed count shows nothing.
 */
const fleetBeaconHttp = axios.create({
  baseURL: getFleetApiBaseURL(),
  timeout: 10_000,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
})
attachRequestInterceptor(fleetBeaconHttp, { ownSession: true })

/**
 * Inbox API HTTP client (in-house email sink, dev/staging only).
 * Sends a static `X-Test-Api-Key` header instead of the Okta JWT
 * because the sink's auth model is a shared secret per environment,
 * not per-user. Okta still gates access at the ADX UI layer.
 */
const inboxApiHttp = axios.create({
  baseURL: getInboxApiBaseURL(),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Test-Api-Key': getInboxApiKey()
  }
})
attachResponseInterceptor(inboxApiHttp, { skipAuthHandling: true })

// Legacy default export (uses V3 API)
const tattleHttp = v3ApiHttp

/**
 * Mint (or reuse) a partner-impersonation bearer token.
 *
 * The Partners API stopped honouring the `X-Admin-As-Partner` header when
 * `AdminImpersonationMiddleware` was removed; admins now exchange their own
 * token for a short-lived, merchant-scoped partner token. Requires the
 * `PartnerImpersonate` admin permission. The cache lives in its own leaf module
 * so the auth store can clear it on logout without importing this one.
 *
 * `scopes` asks for a token carrying fewer scopes than the partner holds, which is
 * how an admin sees what a more constrained partner would get back. The partner's
 * own scopes are never changed. Omit it for the partner's full set; an empty array
 * is a token with no scopes, which is a different request and not the same thing.
 */
const mintPartnerImpersonationToken = async (
  partnerRef: string,
  merchantId: number,
  scopes?: readonly string[]
): Promise<string> => {
  const cached = getPartnerImpersonationToken(partnerRef, merchantId, scopes)
  if (cached) return cached

  // Joins an in-flight mint rather than starting a second one. The cache is only
  // written once the round trip finishes, so concurrent callers would otherwise
  // both miss and both POST.
  return mintOnce(
    partnerRef,
    merchantId,
    async () => {
      const response = await partnersApiHttp.post<{
        token: string
        expiresAt: string
        partner: { scopes: string[] }
      }>(
        `/admin/partner-integrators/${encodeURIComponent(partnerRef)}/${merchantId}/impersonate`,
        scopes === undefined ? {} : { scopes: [...scopes] }
      )

      // An API that predates narrowing ignores the body and mints the full set, which
      // would read as a passing test on permissions the token was meant to lack. The
      // response reports what the token carries, so check it rather than assume.
      if (scopes !== undefined) {
        const granted = [...(response.data.partner?.scopes ?? [])].sort()
        const asked = [...scopes].sort()
        if (granted.length !== asked.length || granted.some((s, i) => s !== asked[i])) {
          throw new Error(
            `Impersonation token was minted with ${granted.join(', ') || 'no scopes'}, ` +
              `not the requested ${asked.join(', ')}. Anything tested with it would be ` +
              'testing the wrong permissions.'
          )
        }
      }

      setPartnerImpersonationToken(
        partnerRef,
        merchantId,
        response.data.token,
        response.data.expiresAt,
        scopes
      )

      return response.data.token
    },
    scopes
  )
}

/**
 * Create a Partners API client that acts as a partner, for one merchant.
 *
 * Each request carries a minted impersonation token rather than the admin's own
 * token — the admin token is only used to mint, and is never sent to a partner
 * endpoint (it would 401 there, as it is not a partner principal).
 *
 * @param partnerRef The partner reference to impersonate
 * @param merchantId The merchant the impersonation is scoped to
 * @param scopes Mint the token with only these scopes, for testing a constrained
 *   partner. Omit for the partner's full set.
 * @returns An axios instance configured for partner impersonation
 */
const createPartnerImpersonationClient = (
  partnerRef: string,
  merchantId: number,
  scopes?: readonly string[]
) => {
  const client = axios.create({
    baseURL: getPartnersApiBaseURL(),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  })

  // Deliberately NOT attachRequestInterceptor — that attaches the admin's own
  // bearer, which the minted token replaces.
  client.interceptors.request.use(async config => {
    const token = await mintPartnerImpersonationToken(partnerRef, merchantId, scopes)
    config.headers.Authorization = `Bearer ${token}`
    return config
  })

  attachResponseInterceptor(client, { skipAuthHandling: true })

  return client
}

export default tattleHttp
export {
  v3ApiHttp,
  integrationsApiHttp,
  partnersApiHttp,
  tasksApiHttp,
  surveysApiHttp,
  fleetApiHttp,
  fleetBeaconHttp,
  inboxApiHttp,
  createPartnerImpersonationClient
}
